import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import { useSEO, createOrganizationSchema, createWebPageSchema } from '../../utils/seo';
import { blogPosts } from '../../mocks/blogData';

export default function BlogPage() {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useSEO({
    title: 'Blog - AXIOM Kalite Kontrol ve İnsan Kaynakları Danışmanlığı',
    description: 'AXIOM blog sayfasında kalite kontrol, insan kaynakları ve kurumsal gelişim konularında güncel içerikler bulabilirsiniz.',
    keywords: 'blog, kalite kontrol, insan kaynakları, kurumsal gelişim, makaleler',
    canonical: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/blog`,
    schema: {
      '@graph': [
        createOrganizationSchema(),
        createWebPageSchema(
          'Blog - AXIOM',
          'AXIOM blog sayfasında kalite kontrol, insan kaynakları ve kurumsal gelişim konularında güncel içerikler.'
        )
      ]
    }
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const categories = ['Tümü', 'Kalite Kontrol', 'İnsan Kaynakları', 'Eğitim'];
  const [selectedCategory, setSelectedCategory] = useState('Tümü');

  const filteredPosts = selectedCategory === 'Tümü' 
    ? blogPosts 
    : blogPosts.filter(post => post.category === selectedCategory);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <header>
        <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white shadow-sm'}`}>
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <Link to="/" className="flex items-center gap-3">
                <img src="https://static.readdy.ai/image/d71986f07c85c7b2b8264ab25e46d42e/edcc2fca5481151c158722794d786f43.png" alt="AXIOM Logo" className="h-12 w-12 object-contain" />
                <h1 className="text-xl font-bold text-gray-900">AXIOM</h1>
              </Link>
              
              {/* Desktop Menu */}
              <div className="hidden md:flex items-center gap-8">
                <Link to="/" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors">{t('nav_home')}</Link>
                <Link to="/about" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors">{t('nav_about')}</Link>
                <Link to="/services" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors">{t('nav_services')}</Link>
                <Link to="/training" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors">{t('nav_training')}</Link>
                <Link to="/blog" className="text-sm font-medium text-teal-600 hover:text-teal-700 transition-colors">{t('nav_blog')}</Link>
                <Link to="/career" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors">Kariyer</Link>
                <Link to="/contact" className="bg-teal-600 text-white px-6 py-2 rounded-full text-sm font-medium hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer">{t('nav_contact')}</Link>
                <LanguageSwitcher />
              </div>

              {/* Mobile Menu Button */}
              <button 
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden w-10 h-10 flex items-center justify-center text-gray-900 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
              >
                <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
              </button>
            </div>

            {/* Mobile Menu */}
            {isMobileMenuOpen && (
              <div className="md:hidden mt-4 pb-4 border-t border-gray-200 pt-4">
                <div className="flex flex-col gap-2">
                  <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors px-3 py-2 rounded-lg hover:bg-teal-50">{t('nav_home')}</Link>
                  <Link to="/about" onClick={() => setIsMobileMenuOpen(false)} className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors px-3 py-2 rounded-lg hover:bg-teal-50">{t('nav_about')}</Link>
                  <Link to="/services" onClick={() => setIsMobileMenuOpen(false)} className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors px-3 py-2 rounded-lg hover:bg-teal-50">{t('nav_services')}</Link>
                  <Link to="/training" onClick={() => setIsMobileMenuOpen(false)} className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors px-3 py-2 rounded-lg hover:bg-teal-50">{t('nav_training')}</Link>
                  <Link to="/blog" onClick={() => setIsMobileMenuOpen(false)} className="text-sm font-medium text-teal-600 hover:text-teal-700 transition-colors px-3 py-2 rounded-lg hover:bg-teal-50">{t('nav_blog')}</Link>
                  <Link to="/career" onClick={() => setIsMobileMenuOpen(false)} className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors px-3 py-2 rounded-lg hover:bg-teal-50">Kariyer</Link>
                  <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)} className="bg-teal-600 text-white px-6 py-2 rounded-full text-sm font-medium hover:bg-teal-700 transition-colors text-center whitespace-nowrap cursor-pointer">{t('nav_contact')}</Link>
                  <div className="px-3 py-2">
                    <LanguageSwitcher />
                  </div>
                </div>
              </div>
            )}
          </div>
        </nav>
      </header>

      <main>
        {/* Hero Section */}
        <section className="relative pt-32 pb-24 bg-gradient-to-br from-teal-50 via-white to-emerald-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 bg-teal-100 rounded-full px-4 py-2 mb-6">
                <i className="ri-article-line text-teal-600 text-sm"></i>
                <span className="text-teal-700 text-sm font-semibold">Blog</span>
              </div>
              <h1 className="text-6xl md:text-7xl font-bold text-gray-900 mb-6 leading-tight">
                {t('blog_title')}
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                {t('blog_subtitle')}
              </p>
            </div>

            {/* Category Filter */}
            <div className="flex items-center justify-center gap-3 flex-wrap">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-6 py-2 rounded-full text-sm font-medium transition-all cursor-pointer ${
                    selectedCategory === category
                      ? 'bg-teal-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Blog Grid */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogPosts.map((post) => (
                <Link 
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  className="group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer"
                >
                  <div className="relative h-64 w-full overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="bg-teal-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                        {post.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line"></i>
                        {post.date}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line"></i>
                        {post.readTime}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-teal-600 transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-gray-600 mb-4 line-clamp-2">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">
                        <i className="ri-user-line mr-1"></i>
                        {post.author}
                      </span>
                      <span className="text-teal-600 font-medium group-hover:gap-2 flex items-center gap-1 transition-all whitespace-nowrap">
                        Devamını Oku
                        <i className="ri-arrow-right-line"></i>
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Newsletter Section */}
        <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-5xl font-bold text-gray-900 mb-6">Blog Güncellemelerini Kaçırmayın</h2>
            <p className="text-xl text-gray-600 mb-8">
              Yeni içeriklerimizden haberdar olmak için e-posta listemize katılın.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 max-w-md mx-auto">
              <input 
                type="email"
                placeholder="E-posta adresiniz"
                className="w-full px-6 py-4 rounded-lg border border-gray-300 focus:border-teal-600 focus:ring-2 focus:ring-teal-100 outline-none transition-all text-sm"
              />
              <button className="w-full sm:w-auto bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer">
                Abone Ol
              </button>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 mb-12">
            <div className="lg:col-span-2">
              <Link to="/" className="flex items-center gap-3 mb-4">
                <img src="https://static.readdy.ai/image/d71986f07c85c7b2b8264ab25e46d42e/edcc2fca5481151c158722794d786f43.png" alt="AXIOM Logo" className="h-10 w-10 object-contain" />
                <span className="text-2xl font-bold">AXIOM</span>
              </Link>
              <p className="text-gray-400 text-sm leading-relaxed mb-6">
                {t('footer_description')}
              </p>
              <div className="flex gap-4">
                <a href="https://www.linkedin.com/in/axiom-kalite" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-teal-600 transition-colors cursor-pointer">
                  <i className="ri-linkedin-fill text-lg"></i>
                </a>
                <a href="https://x.com/AxiomKalite" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-teal-600 transition-colors cursor-pointer">
                  <i className="ri-twitter-x-fill text-lg"></i>
                </a>
                <a href="https://www.instagram.com/axiomkalite/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-teal-600 transition-colors cursor-pointer">
                  <i className="ri-instagram-fill text-lg"></i>
                </a>
                <a href="https://www.facebook.com/profile.php?id=61585433974202" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-teal-600 transition-colors cursor-pointer">
                  <i className="ri-facebook-fill text-lg"></i>
                </a>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold text-lg mb-4">{t('footer_services')}</h4>
              <ul className="space-y-3">
                <li><Link to="/services" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_quality')}</Link></li>
                <li><Link to="/services" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_hr')}</Link></li>
                <li><Link to="/training" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_training')}</Link></li>
                <li><Link to="/services" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_audit')}</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-lg mb-4">{t('footer_corporate')}</h4>
              <ul className="space-y-3">
                <li><Link to="/about" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_about')}</Link></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_team')}</a></li>
                <li><Link to="/career" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_career')}</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_contact')}</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-lg mb-4">{t('footer_resources')}</h4>
              <ul className="space-y-3">
                <li><Link to="/blog" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_blog')}</Link></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_references')}</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_faq')}</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_documents')}</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-gray-400">
              <p>{t('footer_copyright')}</p>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">
                {t('footer_powered')}
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}